import type { ApiErrorBody } from './api';
import type { Capability, SystemRoleKey } from './permissions';

export const GHALISHOR_API_V1_PREFIX = '/api/v1' as const;
export const GHALISHOR_REFRESH_COOKIE_NAME = '__Host-ghalishor_refresh' as const;
export const GHALISHOR_BUSINESS_ID_HEADER = 'X-Ghalishor-Business-Id' as const;
export const GHALISHOR_IDEMPOTENCY_HEADER = 'Idempotency-Key' as const;

export const IDENTITY_ACCESS_ROUTES = Object.freeze({
  otpRequest: `${GHALISHOR_API_V1_PREFIX}/auth/otp/request`,
  otpVerify: `${GHALISHOR_API_V1_PREFIX}/auth/otp/verify`,
  refresh: `${GHALISHOR_API_V1_PREFIX}/auth/refresh`,
  impersonationStart: `${GHALISHOR_API_V1_PREFIX}/auth/impersonation/start`,
  impersonationStop: `${GHALISHOR_API_V1_PREFIX}/auth/impersonation/stop`,
  logout: `${GHALISHOR_API_V1_PREFIX}/auth/logout`,
  logoutAll: `${GHALISHOR_API_V1_PREFIX}/auth/logout-all`,
  me: `${GHALISHOR_API_V1_PREFIX}/auth/me`,
  businesses: `${GHALISHOR_API_V1_PREFIX}/businesses`,
  businessContext: (businessId: string) =>
    `${GHALISHOR_API_V1_PREFIX}/businesses/${encodeURIComponent(businessId)}/context`,
  businessMemberships: (businessId: string) =>
    `${GHALISHOR_API_V1_PREFIX}/businesses/${encodeURIComponent(businessId)}/memberships`,
});

export const GHALISHOR_IDENTITY_ACCESS_ERROR_CODES = Object.freeze([
  'GHALISHOR_AUTH_REQUIRED',
  'GHALISHOR_AUTH_TOKEN_INVALID',
  'GHALISHOR_AUTH_SESSION_EXPIRED',
  'GHALISHOR_AUTH_SESSION_REVOKED',
  'GHALISHOR_AUTH_ORIGIN_REJECTED',
  'GHALISHOR_REFRESH_INVALID',
  'GHALISHOR_REFRESH_REUSE_DETECTED',
  'GHALISHOR_OTP_REQUEST_REJECTED',
  'GHALISHOR_OTP_INVALID',
  'GHALISHOR_OTP_EXPIRED',
  'GHALISHOR_OTP_ATTEMPTS_EXHAUSTED',
  'GHALISHOR_PASSWORD_INVALID',
  'GHALISHOR_PASSWORD_ACCOUNT_EXISTS',
  'GHALISHOR_PLATFORM_OWNER_RESERVED',
  'GHALISHOR_IMPERSONATION_FORBIDDEN',
  'GHALISHOR_IMPERSONATION_INVALID_TARGET',
  'GHALISHOR_PASSWORD_RESET_INVALID',
  'GHALISHOR_EMAIL_DELIVERY_UNAVAILABLE',
  'GHALISHOR_BUSINESS_CONTEXT_REQUIRED',
  'GHALISHOR_BUSINESS_CONTEXT_MISMATCH',
  'GHALISHOR_BUSINESS_UNAVAILABLE',
  'GHALISHOR_MEMBERSHIP_REQUIRED',
  'GHALISHOR_MEMBERSHIP_INACTIVE',
  'GHALISHOR_CAPABILITY_REQUIRED',
  'GHALISHOR_IDEMPOTENCY_REQUIRED',
  'GHALISHOR_IDEMPOTENCY_CONFLICT',
  'GHALISHOR_BUSINESS_NAME_INVALID',
  'GHALISHOR_BUSINESS_CREATION_REJECTED',
  'GHALISHOR_VALIDATION_FAILED',
  'GHALISHOR_INTERNAL_ERROR',
] as const);

export type GhalishorIdentityAccessErrorCode =
  (typeof GHALISHOR_IDENTITY_ACCESS_ERROR_CODES)[number];

const IDENTITY_ACCESS_ERROR_CODE_SET: ReadonlySet<string> = new Set(
  GHALISHOR_IDENTITY_ACCESS_ERROR_CODES,
);

export function isGhalishorIdentityAccessErrorCode(
  value: unknown,
): value is GhalishorIdentityAccessErrorCode {
  return typeof value === 'string' && IDENTITY_ACCESS_ERROR_CODE_SET.has(value);
}

export type IdentityAccessApiErrorBody = Omit<ApiErrorBody, 'code'> & {
  readonly code: GhalishorIdentityAccessErrorCode;
};

export type UuidString = string;
export type IsoDateTimeString = string;
export type E164MobileString = string;
export type OpaqueAccessToken = string;

export type UserStatus = 'active' | 'inactive';
export type BusinessStatus = 'pending' | 'active' | 'suspended' | 'closed';
export type MembershipStatus = 'active' | 'inactive';

export interface IdentityUserSummary {
  readonly id: UuidString;
  readonly mobileE164: E164MobileString;
  readonly status: UserStatus;
  readonly createdAt: IsoDateTimeString;
}

export interface IdentityBusinessSummary {
  readonly id: UuidString;
  readonly slug: string;
  readonly name: string;
  readonly status: BusinessStatus;
  readonly ownerUserId: UuidString;
}

export interface IdentityMembershipSummary {
  readonly id: UuidString;
  readonly businessId: UuidString;
  readonly businessName: string;
  readonly businessStatus: BusinessStatus;
  readonly userId: UuidString;
  readonly status: MembershipStatus;
  readonly isPrimaryOwner: boolean;
  readonly roleKeys: readonly SystemRoleKey[];
}

export interface IdentitySessionSummary {
  readonly id: UuidString;
  readonly createdAt: IsoDateTimeString;
  readonly accessExpiresAt: IsoDateTimeString;
  readonly refreshExpiresAt: IsoDateTimeString;
  readonly lastUsedAt: IsoDateTimeString | null;
  readonly userAgent: string | null;
}

export type ImpersonationIntent = 'customer' | 'business';

export interface ImpersonationSummary {
  readonly active: true;
  readonly ownerUserId: UuidString;
  readonly ownerMobileE164: E164MobileString;
  readonly targetUserId: UuidString;
  readonly targetMobileE164: E164MobileString;
  readonly targetDisplayName: string | null;
  readonly intent: ImpersonationIntent;
  readonly businessId: UuidString | null;
  readonly startedAt: IsoDateTimeString;
}

export interface StartImpersonationRequest {
  readonly targetUserId: UuidString;
  readonly intent: ImpersonationIntent;
  readonly businessId?: UuidString;
}

export interface ImpersonationSessionResponse extends AccessGrantResponse {
  readonly user: IdentityUserSummary;
  readonly memberships: readonly IdentityMembershipSummary[];
  readonly impersonation: ImpersonationSummary | null;
}

export interface RequestOtpRequest {
  readonly mobile: string;
  readonly autofill?: "web" | "android" | "none";
}

export interface RequestOtpResponse {
  readonly message: string;
  readonly challengeId: UuidString;
  readonly expiresAt: IsoDateTimeString;
  readonly resendAfter: IsoDateTimeString;
  readonly _devCode?: string;
}

export interface VerifyOtpRequest {
  readonly challengeId: UuidString;
  readonly mobile: string;
  readonly code: string;
}

export interface AccessGrantResponse {
  readonly accessToken: OpaqueAccessToken;
  readonly accessExpiresAt: IsoDateTimeString;
  /** Present for native mobile clients (`X-Ghalishor-Client: mobile`). */
  readonly refreshToken?: string;
}

export interface VerifyOtpResponse extends AccessGrantResponse {
  readonly user: IdentityUserSummary;
  readonly memberships: readonly IdentityMembershipSummary[];
}

export interface RefreshSessionResponse extends AccessGrantResponse {
  readonly session: IdentitySessionSummary;
}

export interface LogoutResponse {
  readonly success: true;
}

export interface LogoutAllResponse {
  readonly success: true;
  readonly revokedSessionCount: number;
}

export interface CurrentUserResponse {
  readonly user: IdentityUserSummary;
  readonly session: IdentitySessionSummary;
  readonly memberships: readonly IdentityMembershipSummary[];
  readonly impersonation: ImpersonationSummary | null;
}

export interface ListBusinessesResponse {
  readonly memberships: readonly IdentityMembershipSummary[];
}

export interface CreateBusinessRequest {
  readonly name: string;
}

export interface CreateBusinessResponse {
  readonly business: IdentityBusinessSummary;
  readonly membership: IdentityMembershipSummary;
  readonly ownerRole: {
    readonly id: UuidString;
    readonly key: 'owner';
    readonly isSystem: true;
  };
  readonly effectiveCapabilities: readonly Capability[];
}

export interface BusinessContextResponse {
  readonly business: IdentityBusinessSummary;
  readonly membership: IdentityMembershipSummary;
  readonly effectiveCapabilities: readonly Capability[];
}

export interface BusinessMembershipListItem {
  readonly id: UuidString;
  readonly businessId: UuidString;
  readonly user: IdentityUserSummary;
  readonly status: MembershipStatus;
  readonly isPrimaryOwner: boolean;
  readonly roleKeys: readonly SystemRoleKey[];
}

export interface ListBusinessMembershipsResponse {
  readonly memberships: readonly BusinessMembershipListItem[];
}
